import cls from "@fvilers/cls";
import {
  Children,
  ComponentPropsWithRef,
  Ref,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { Link, LinkProps } from "react-router";
import { TouchTarget } from "../TouchTarget/TouchTarget";
import styles from "./Button.module.css";

export type ButtonProps = (
  | ({
      as: typeof Link;
    } & ComponentPropsWithRef<typeof Link>)
  | ({
      as?: "button";
      to?: never;
    } & ComponentPropsWithRef<"button">)
) &
  ButtonVariants;

export type ButtonVariants = {
  color?: "accent-1" | "accent-2" | "neutral" | "primary" | "secondary";
  disabled?: boolean;
  icon?: boolean;
  loading?: boolean;
  size?: "md" | "sm";
  variant?: "contained" | "ghost" | "outline" | "underlined";
};

export function Button({
  as,
  children,
  className,
  color = "primary",
  icon,
  loading,
  size = "md",
  variant = "contained",
  ...rest
}: ButtonProps) {
  const classes = cls(
    styles.Button,
    styles[`color-${color}`],
    styles[`variant-${variant}`],
    styles[`size-${size}`],
    loading && styles.loading,
    icon && styles[`icon-${icon}`],
    className,
  );
  const normalizedChildren = Children.map(children, (child) =>
    typeof child === "string" ? <span>{child}</span> : child,
  );
  const isLink = as === Link && !rest.disabled;
  const userRef = rest.ref as Ref<HTMLAnchorElement> | undefined;
  const linkRef = useRef<HTMLAnchorElement>(null);
  const mergedLinkRef = useCallback(
    (el: HTMLAnchorElement | null) => {
      linkRef.current = el;
      return setRef(userRef, el);
    },
    [userRef],
  );

  // React only applies autoFocus to form elements, so focus links manually.
  useEffect(() => {
    if (isLink && rest.autoFocus) {
      linkRef.current?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLink) {
    const { to, ...linkProps } = rest as LinkProps;

    return (
      <Link
        {...linkProps}
        className={classes}
        data-autofocus={linkProps.autoFocus || undefined}
        data-size={size}
        ref={mergedLinkRef}
        to={to}
      >
        <TouchTarget>{normalizedChildren}</TouchTarget>
      </Link>
    );
  }

  return (
    <button
      type="button"
      {...(rest as ComponentPropsWithRef<"button">)}
      className={classes}
      data-autofocus={rest.autoFocus || undefined}
      data-size={size}
    >
      {normalizedChildren}
    </button>
  );
}

function setRef<T>(ref: Ref<T> | undefined, value: T | null) {
  if (typeof ref === "function") {
    return ref(value);
  }
  if (ref) {
    ref.current = value;
  }
}
